import React from 'react';
import { Link } from 'react-router5';
import { Route, useRoute } from 'config/routes';
import { isNumber } from 'lodash';
import { useLocale } from 'locale';
import { PricesTabType, HistoryHebrewItem } from 'store/state/selectors/insights/prices';
import { ColumnRendererProps, EMPTY_COL_CHAR, PricesTableColumn } from '../../../utils';
import { HistoryPricesColumn, DateLabel, AddressLabel, DateColumnMobileWrapper, MobileLabelsWrapper } from './styled';
import { SeparatorWrapper, ThisUnitLabelHistory } from 'components/widget/prices/prices-table/tables/history/styled';
import { useScreenBreakpoint } from 'consts/breakpoints';

type HistoryHebrewDateColumnProps =  ColumnRendererProps<HistoryHebrewItem> & {
  isCurrent?: boolean;
  isProjectPage?: boolean;
  projectId: string;
  isAddressMasked?: boolean;
  nofollow?: string;
};

export const HistoryHebrewDateColumn: React.FC<HistoryHebrewDateColumnProps> = ({
  item,
  isMobile,
  tabId,
  isCurrent,
  isProjectPage,
  projectId,
  isAddressMasked,
  nofollow,
}) => {
  const { t, formatDate, formatMoney } = useLocale();
  const screen = useScreenBreakpoint();
  const { route } = useRoute();
  const { units = [], beds, size, floor, distance, year } = item;
  const isCompare = tabId === PricesTabType.Compare;
  const includeDistance = route.name !== Route.StreetPage && route.name !== Route.LocalPage && !isCompare && tabId !== PricesTabType.CompareDeals;

  const addressText = item.address ? item.address : t('widget.prices.unknownAddress');
  const isCurrentProject = item.projectIds && projectId && item.projectIds.includes(projectId);
  const addressLabel = (
    <AddressLabel title={item.address} style={{ textTransform: 'capitalize' }}>
      {t('widget.prices.addressLabel', {
        hasProjectMultipleIds: false,
        isCurrentProject,
        projectName: addressText,
      })}
    </AddressLabel>
  );

  const address = isCompare && item.address ? (
    <Link routeName={Route.UnitPage} routeParams={{ id: item.id }}>
      {addressLabel}
    </Link>
  ) : (
    isAddressMasked && isCurrent && isMobile ? null : (
      <Link routeName={Route.Sold} routeParams={{ id: item.id }} rel={nofollow}>
        {addressLabel}
      </Link>
    )
  );

  const distanceLabel =
    isNumber(distance) && !isCurrent && includeDistance && !(isProjectPage && distance === 0)
      ? t('widget.prices.distance', { distance, isMobile: true })
      : null;
  return (
    <>
      <HistoryPricesColumn
        type={tabId}
        isBulk={Boolean(units.length)}
        col={PricesTableColumn.Date}
      >
        <DateLabel>{isCurrent && isMobile ? (
          <ThisUnitLabelHistory>
            {item.currentPoiLabel}
          </ThisUnitLabelHistory>
        ) : item.date ? formatDate(item.date) : EMPTY_COL_CHAR}</DateLabel>
        {isMobile ? (
          <DateColumnMobileWrapper>
            {
              [
                address,
                distanceLabel,
                (size ? t('widget.prices.size', { size }) : null),
                (item.pricePerSqFt
                  ? screen === 1
                    ? t('widget.prices.pricePerSqMeter', { price: formatMoney(item.pricePerSqFt) })
                    : formatMoney(item.pricePerSqFt)
                  : null),
                (beds ? t('widget.prices.rooms', { rooms: beds }) : null),
                (isNumber(floor) ? t('widget.prices.floor', { floor }) : null),
                (year ? `${t('widget.prices.columns.year')}-${year}` : null),
              ]
              .filter(Boolean)
              .map((i, idx) => <MobileLabelsWrapper key={idx}>{i}</MobileLabelsWrapper>)
              .reduce((prev, curr) => <>{prev}<SeparatorWrapper>•</SeparatorWrapper>{curr}</>)
            }
          </DateColumnMobileWrapper>
        ) : null}
      </HistoryPricesColumn>
    </>
  );
};
